import axiosInstance from '../config/axios.config';

/**
 * Lead Sharing Settings Service
 * Handles API calls for managing lead sharing configuration
 */
const leadSharingSettingsService = {
    // ========================
    // TENANT ADMIN APIs
    // ========================

    /**
     * Get all companies with their sharing settings (Tenant Admin only)
     * @param {Object} params - Query parameters (search, page, limit, sort_field, sort_order)
     */
    getCompaniesWithSharingSettings: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/admin/companies/sharing-settings', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching companies sharing settings:', error);
            throw error;
        }
    },

    /**
     * Get single company details (Tenant Admin only)
     * @param {number} companyId
     */
    getCompanyDetails: async (companyId) => {
        try {
            const response = await axiosInstance.get(`/admin/companies/${companyId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching company details:', error);
            throw error;
        }
    },

    /**
     * Update company sharing settings (Tenant Admin only)
     * @param {number} companyId
     * @param {Object} settings - { cross_company_sharing_enabled, cross_company_receiving_enabled }
     */
    updateCompanySharingSettings: async (companyId, settings) => {
        try {
            const response = await axiosInstance.put(`/admin/companies/${companyId}/sharing-settings`, settings);
            return response.data;
        } catch (error) {
            console.error('Error updating company sharing settings:', error);
            throw error;
        }
    },

    /**
     * Bulk update company sharing settings (Tenant Admin only)
     * @param {Array} companies - Array of { id, cross_company_sharing_enabled, cross_company_receiving_enabled }
     */
    bulkUpdateCompanySharingSettings: async (companies) => {
        try {
            const response = await axiosInstance.post('/admin/companies/sharing-settings/bulk-update', { companies });
            return response.data;
        } catch (error) {
            console.error('Error bulk updating company sharing settings:', error);
            throw error;
        }
    },

    // ========================
    // COMPANY ADMIN APIs
    // ========================

    /**
     * Get employee sharing settings (Company Admin only)
     * @param {number} employeeId
     */
    getEmployeeSharingSettings: async (employeeId) => {
        try {
            const response = await axiosInstance.get(`/company/employees/${employeeId}/sharing-settings`);
            return response.data;
        } catch (error) {
            console.error('Error fetching employee sharing settings:', error);
            throw error;
        }
    },

    /**
     * Update employee colleague sharing setting (Company Admin only)
     * @param {number} employeeId
     * @param {boolean} colleagueSharingEnabled
     */
    updateEmployeeSharingSettings: async (employeeId, colleagueSharingEnabled) => {
        try {
            const response = await axiosInstance.put(`/company/employees/${employeeId}/sharing-settings`, {
                colleague_sharing_enabled: colleagueSharingEnabled
            });
            return response.data;
        } catch (error) {
            console.error('Error updating employee sharing settings:', error);
            throw error;
        }
    },

    /**
     * Bulk update employee sharing settings (Company Admin only)
     * @param {Array} employees - Array of { id, colleague_sharing_enabled }
     */
    bulkUpdateEmployeeSharingSettings: async (employees) => {
        try {
            const response = await axiosInstance.post('/company/employees/sharing-settings/bulk-update', { employees });
            return response.data;
        } catch (error) {
            console.error('Error bulk updating employee sharing settings:', error);
            throw error;
        }
    },

    // ========================
    // SHAREABLE USERS API
    // ========================

    /**
     * Get shareable users with role-based filtering
     * @param {Object} params - { search, category, page, limit }
     */
    getShareableUsers: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/leads/shareable-users', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching shareable users:', error);
            throw error;
        }
    }
};

export default leadSharingSettingsService;
